import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "../lib/api";
import { formatGBP } from "../lib/format";
import { ArrowLeft, Star, X, CheckCircle, Clock, ChatCircleDots } from "@phosphor-icons/react";
import { WhatsAppShareButton } from "../components/WhatsAppWidget";
import { toast } from "sonner";

const STATUSES = ["pending", "accepted", "preparing", "dispatched", "delivered"];

export default function OrderDetail() {
  const { order_id } = useParams();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showRate, setShowRate] = useState(false);
  const [stars, setStars] = useState(5);
  const [comment, setComment] = useState("");
  const [existingRating, setExistingRating] = useState(null);

  const load = async () => {
    try {
      const { data } = await api.get(`/orders/${order_id}`);
      setOrder(data);
    } catch (err) { /* eslint-disable-next-line no-console */ console.warn("[OrderDetail] non-fatal error:", err); }
    setLoading(false);
  };

  useEffect(() => { load(); }, [order_id]);

  const cancel = async () => {
    if (!confirm("Cancel this order?")) return;
    try {
      await api.post(`/orders/${order_id}/cancel`);
      toast.success("Order cancelled");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancellation failed");
    }
  };

  const submitRating = async () => {
    try {
      await api.post("/ratings", { order_id, stars, comment });
      toast.success("Thanks for your review!");
      setExistingRating({ stars, comment });
      setShowRate(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit rating");
    }
  };

  if (loading || !order) return <div className="min-h-screen flex items-center justify-center text-sm text-[#5C5C54]">Loading...</div>;

  const currentIndex = STATUSES.indexOf(order.status);
  const canCancel = ["pending", "accepted"].includes(order.status);
  const canRate = order.status === "delivered" && !existingRating;
  const eta = order.estimated_delivery_at ? new Date(order.estimated_delivery_at) : null;

  return (
    <div className="pt-24 pb-24 max-w-4xl mx-auto px-4 md:px-8" data-testid="order-detail-page">
      <Link to="/orders" className="inline-flex items-center gap-2 text-xs font-bold text-[#5C5C54] hover:text-[#A94438] mb-6">
        <ArrowLeft size={14} weight="bold" /> All orders
      </Link>

      <div className="card-surface p-6 md:p-8 mb-6">
        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-6">
          <div>
            <p className="text-xs text-[#5C5C54] mb-1">Order #{order.order_id.slice(-10)}</p>
            <h1 className="font-heading text-2xl md:text-3xl font-black tracking-tighter">{order.vendor_name}</h1>
            <p className="text-xs text-[#5C5C54] mt-1">Placed {new Date(order.created_at).toLocaleString("en-GB")}</p>
            {eta && order.status !== "cancelled" && order.status !== "delivered" && (
              <p className="mt-2 inline-flex items-center gap-1.5 text-xs font-bold text-[#2B5329] bg-[#2B5329]/10 border border-[#2B5329]/30 rounded-full px-3 py-1.5" data-testid="order-eta-badge">
                <Clock size={14} weight="duotone" /> Estimated arrival: {eta.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })}
              </p>
            )}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <WhatsAppShareButton
              message={`Hi AfriPantry, I have a question about order #${order.order_id.slice(-8)}.`}
              label="Chat on WhatsApp"
              testid="order-whatsapp-help"
            />
            <Link to="/support" data-testid="order-contact-support" className="btn-secondary text-xs gap-1">
              <ChatCircleDots size={14} weight="duotone" /> Support
            </Link>
            {canCancel && (
              <button data-testid="cancel-order-btn" onClick={cancel} className="btn-secondary text-xs gap-1">
                <X size={14} weight="bold" /> Cancel
              </button>
            )}
          </div>
        </div>

        {/* STATUS TRACKER */}
        {order.status !== "cancelled" && (
          <div className="mb-8" data-testid="order-status-tracker">
            <div className="flex items-center justify-between mb-2">
              {STATUSES.map((s, i) => (
                <div key={s} className="flex-1 flex flex-col items-center">
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${i <= currentIndex ? "bg-[#2B5329] text-white" : "bg-[#F2EFE9] text-[#5C5C54] border border-[#E5E2D9]"}`}>
                    {i <= currentIndex ? <CheckCircle size={16} weight="fill" /> : i + 1}
                  </div>
                  <p className={`text-[10px] font-bold mt-2 capitalize text-center ${i <= currentIndex ? "text-[#1A1A18]" : "text-[#5C5C54]"}`}>{s}</p>
                </div>
              ))}
            </div>
            <div className="h-1 bg-[#F2EFE9] rounded-full overflow-hidden relative">
              <div className="absolute left-0 top-0 h-full bg-[#2B5329] transition-all" style={{ width: `${(currentIndex / (STATUSES.length - 1)) * 100}%` }} />
            </div>
          </div>
        )}

        {order.status === "cancelled" && (
          <div className="p-4 rounded-xl bg-[#A94438]/10 border border-[#A94438]/30 mb-6">
            <p className="text-sm font-bold text-[#A94438]">This order was cancelled.</p>
          </div>
        )}

        {/* DELIVERY */}
        <div className="grid md:grid-cols-2 gap-6 mb-6 text-sm">
          <div>
            <p className="text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-2">Delivery address</p>
            <p>{order.delivery_address?.line1}</p>
            {order.delivery_address?.line2 && <p>{order.delivery_address.line2}</p>}
            <p>{order.delivery_address?.city}, {order.delivery_address?.postcode}</p>
            {order.delivery_notes && <p className="text-xs text-[#5C5C54] mt-2 italic">"{order.delivery_notes}"</p>}
          </div>
          <div>
            <p className="text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-2">Estimated arrival</p>
            {order.estimated_delivery_at ? (
              <p>{new Date(order.estimated_delivery_at).toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" })}</p>
            ) : (
              <p className="text-[#5C5C54]">TBD</p>
            )}
          </div>
        </div>

        {/* ITEMS */}
        <div className="border-t border-[#E5E2D9] pt-5">
          <p className="text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-3">Items ({order.items?.length || 0})</p>
          <div className="space-y-3">
            {order.items?.map((i) => (
              <div key={i.item_id} className="flex items-center gap-3">
                <div className="w-14 h-14 rounded-xl overflow-hidden bg-[#F2EFE9]">
                  {i.image_url && <img src={i.image_url} alt={i.name} className="w-full h-full object-cover" />}
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-sm truncate">{i.name}</p>
                  <p className="text-xs text-[#5C5C54]">{i.quantity} × {formatGBP(i.price)}</p>
                </div>
                <p className="font-bold text-sm">{formatGBP(i.subtotal)}</p>
              </div>
            ))}
          </div>

          <div className="border-t border-[#E5E2D9] mt-5 pt-4 space-y-1.5 text-sm">
            <div className="flex justify-between text-[#5C5C54]"><span>Subtotal</span><span className="font-bold text-[#1A1A18]">{formatGBP(order.subtotal)}</span></div>
            <div className="flex justify-between text-[#5C5C54]"><span>Delivery</span><span className="font-bold text-[#1A1A18]">{formatGBP(order.delivery_fee)}</span></div>
            <div className="flex justify-between items-baseline pt-2 border-t border-[#E5E2D9]">
              <span className="text-xs font-bold tracking-wider uppercase">Total</span>
              <span className="font-heading text-xl font-black">{formatGBP(order.total)}</span>
            </div>
          </div>
        </div>
      </div>

      {/* RATE */}
      {canRate && !showRate && (
        <button data-testid="open-rate-btn" onClick={() => setShowRate(true)} className="card-surface p-5 w-full text-center hover:border-[#A94438] transition-all">
          <Star size={20} weight="duotone" className="inline text-[#E4A951] mr-2" />
          <span className="font-heading font-bold">Rate your experience with {order.vendor_name}</span>
        </button>
      )}

      {showRate && (
        <div className="card-surface p-6" data-testid="rating-form">
          <h2 className="font-heading text-xl font-bold mb-4">How was your order?</h2>
          <div className="flex items-center gap-2 mb-4">
            {[1, 2, 3, 4, 5].map((s) => (
              <button key={s} data-testid={`rate-star-${s}`} onClick={() => setStars(s)} className="hover:scale-110 transition-transform">
                <Star size={30} weight={s <= stars ? "fill" : "regular"} className={s <= stars ? "text-[#E4A951]" : "text-[#E5E2D9]"} />
              </button>
            ))}
          </div>
          <textarea data-testid="rate-comment" rows={3} value={comment} onChange={(e) => setComment(e.target.value)} placeholder="Share your experience (optional)" className="input-field resize-none text-sm mb-4" />
          <div className="flex gap-2">
            <button onClick={submitRating} data-testid="submit-rating-btn" className="btn-primary text-sm">Submit review</button>
            <button onClick={() => setShowRate(false)} className="btn-secondary text-sm">Cancel</button>
          </div>
        </div>
      )}
    </div>
  );
}
